import { AIToolCall } from "@coeff/api";
import React, { useEffect, useRef, useState } from "react";

import { Sparkle2 } from "../../components/Icons";

import { Message } from "./useChatMessages";

type ToolInput = {
  question?: string;
  chart_type?: string;
  range_a1?: string;
  target_range_a1?: string;
  tool_message_summary?: string;
  current_state?: string;
  url?: string;
  direction?: string;
  key?: string;
  endpoint?: string;
};

type CompletedTool = {
  toolCall: AIToolCall;
  reasoning?: string;
  text?: string;
  timestamp: string;
  messageId: string;
};

const PATIENCE_MESSAGES = [
  "Still working on it...",
  "Thanks for your patience...",
  "Almost done. Give me a few more seconds...",
];
const PATIENCE_INTERVAL_MS = 10000;
const PATIENCE_DISPLAY_MS = 5000;

const parseToolInput = (toolCall?: AIToolCall): ToolInput => {
  const toolArgs = toolCall?.function?.arguments ?? "{}";
  try {
    const parsed = JSON.parse(toolArgs);
    return parsed.input ?? parsed ?? {};
  } catch {
    return {};
  }
};

const getToolDoneMessage = (toolCall?: AIToolCall): string => {
  const toolInput = parseToolInput(toolCall);
  const toolMessageSummary = toolInput.tool_message_summary;

  switch (toolCall?.function?.name) {
    case "list_spreadsheet_sqlite_tables":
      return "Analyzed available data tables";
    case "describe_sqlite_table":
      return "Examined table structure";
    case "run_sqlite_query":
      return "Analyzed your data";
    case "generate_or_edit_image":
      return "Generated dashboard image";
    case "record_web_dashboard_plan":
      return "Planned dashboard layout";
    case "build_blocks_and_save_web_dashboard":
      return "Saved your dashboard";
    case "fetch_dashboard_images":
      return "Checked dashboard images";
    case "record_built_block":
      return "Built dashboard block";
    case "validate_web_dashboard_resources":
      return "Validated dashboard components";
    case "save_web_dashboard":
      return "Saved your dashboard";
    case "update_web_dashboard":
      return "Updated dashboard";
    case "search_web":
      return toolInput.question
        ? `Found results for ${toolMessageSummary || toolInput.question}`
        : "Completed web search";
    case "screenshot":
    case "browser_screenshot":
      return "Captured screenshot";
    case "get_page_snapshot":
      return "Analyzed page content";
    case "navigate":
    case "browser_navigate":
      return "Navigated to page";
    case "browser_click":
      return toolMessageSummary || "Clicked element";
    case "browser_type":
      return "Entered text";
    case "browser_select_option":
      return "Selected option";
    case "browser_press_key":
      return toolInput.key ? `Pressed ${toolInput.key}` : "Pressed key";
    case "browser_scroll":
      return toolInput.direction === "up" ? "Scrolled up" : "Scrolled down";
    case "browser_wait":
      return "Waited for page";
    case "setup_dashboard_context":
      return "Dashboard data is ready for exploration";
    default:
      return "";
  }
};

const getToolLoadingMessage = (toolCall?: AIToolCall): string => {
  const toolInput = parseToolInput(toolCall);
  const toolMessageSummary = toolInput.tool_message_summary;

  switch (toolCall?.function?.name) {
    case "list_spreadsheet_sqlite_tables":
      return "Analyzing available data tables";
    case "describe_sqlite_table":
      return "Examining table structure";
    case "run_sqlite_query":
      return "Querying dashboard data";
    case "get_block_spec":
      return "Reading the block";
    case "set_block_explanation":
      return "Generating the explanation";
    case "generate_or_edit_image":
      return "Generating dashboard image";
    case "record_web_dashboard_plan":
      return "Planning dashboard layout";
    case "build_blocks_and_save_web_dashboard":
      return "Building and saving dashboard";
    case "fetch_dashboard_images":
      return "Loading dashboard images";
    case "record_built_block":
      return "Building dashboard block";
    case "validate_web_dashboard_resources":
      return "Validating dashboard components";
    case "save_web_dashboard":
      return "Saving your dashboard";
    case "update_web_dashboard":
      return "Updating dashboard";
    case "update_web_dashboard_building_state":
      if (toolInput.current_state === "exploring_data") {
        return "Exploring the spreadsheet data";
      }
      if (toolInput.current_state === "planning_layout") {
        return "Planning the dashboard layout";
      }
      if (toolInput.current_state === "building_dashboard") {
        return "Building dashboard blocks";
      }
      return "Loading";
    case "search_web":
      return toolInput.question
        ? `Searching for ${toolMessageSummary || toolInput.question}`
        : "Searching the web";
    case "screenshot":
    case "browser_screenshot":
      return "Capturing screenshot";
    case "get_page_snapshot":
      return "Analyzing page content";
    case "navigate":
    case "browser_navigate":
      if (toolInput.url) {
        try {
          return `Navigating to ${new URL(toolInput.url).hostname}`;
        } catch {
          return "Navigating to page";
        }
      }
      return "Navigating to page";
    case "browser_click":
      return toolMessageSummary || "Clicking element";
    case "browser_type":
      return "Entering text";
    case "browser_select_option":
      return "Selecting option";
    case "browser_press_key":
      return toolInput.key ? `Pressing ${toolInput.key}` : "Pressing key";
    case "browser_scroll":
      return toolInput.direction === "up" ? "Scrolling up" : "Scrolling down";
    case "browser_wait":
      return "Waiting for page";
    case "setup_dashboard_context":
      return "Setting up dashboard data";
    default:
      return "Thinking...";
  }
};

const Chevron: React.FC<{ isExpanded: boolean; className?: string }> = ({
  isExpanded,
  className = "h-3 w-3 text-slate-500",
}) => (
  <svg
    className={`shrink-0 transition-transform ${className} ${isExpanded ? "rotate-180" : ""}`}
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

const SPREADSHEET_ANALYSIS_PHASE_TOOL_NAMES = new Set([
  "setup_dashboard_context",
  "list_spreadsheet_sqlite_tables",
  "describe_sqlite_table",
  "run_sqlite_query",
]);

type InFlightToolChain = {
  deduplicatedCompletedTools: CompletedTool[];
  latestToolCall: AIToolCall | undefined;
  latestToolText: string;
};

/** Shared walk of the current assistant tool-call chain (matches ThinkingLoader completed-tool logic). */
function analyzeInFlightToolChain(messages: Message[] | undefined): InFlightToolChain {
  const completedTools: CompletedTool[] = [];
  let latestToolCall: AIToolCall | undefined;
  let latestToolText = "";

  if (messages && messages.length > 0) {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      const toolCalls = msg.content.tool_calls;

      if (!toolCalls || toolCalls.length === 0) {
        break;
      }

      if (i === messages.length - 1) {
        latestToolCall = toolCalls[toolCalls.length - 1];
        for (let j = 0; j < toolCalls.length - 1; j++) {
          completedTools.unshift({
            toolCall: toolCalls[j],
            reasoning: msg.content.reasoning,
            text: msg.content.text,
            timestamp: msg.created_dt,
            messageId: msg.id,
          });
        }
      } else {
        toolCalls.forEach(toolCall => {
          completedTools.unshift({
            toolCall,
            reasoning: msg.content.reasoning,
            text: msg.content.text,
            timestamp: msg.created_dt,
            messageId: msg.id,
          });
        });
      }

      if (!latestToolText && msg.content.text) {
        latestToolText = msg.content.text;
      }
    }
  }

  const deduplicatedCompletedTools = completedTools
    .filter(item => {
      const toolMessage = getToolDoneMessage(item.toolCall);
      return toolMessage && toolMessage !== "Loading";
    })
    .filter((item, index, items) => {
      if (index === 0) {
        return true;
      }
      return getToolDoneMessage(item.toolCall) !== getToolDoneMessage(items[index - 1].toolCall);
    });

  return { deduplicatedCompletedTools, latestToolCall, latestToolText };
}

/**
 * Ordered labels for completed builder milestones (spreadsheet tools collapse to "Analyzed spreadsheet").
 * Uses the same tool ordering/deduping as ThinkingLoader.
 */
export function getBuilderCompletedStepLabels(messages: Message[] | undefined): string[] {
  const { deduplicatedCompletedTools } = analyzeInFlightToolChain(messages);
  const labels: string[] = [];
  let mergedSpreadsheet = false;

  for (const item of deduplicatedCompletedTools) {
    const name = item.toolCall?.function?.name ?? "";
    if (SPREADSHEET_ANALYSIS_PHASE_TOOL_NAMES.has(name)) {
      if (!mergedSpreadsheet) {
        labels.push("Analyzed spreadsheet");
        mergedSpreadsheet = true;
      }
      continue;
    }
    const done = getToolDoneMessage(item.toolCall);
    if (done) {
      labels.push(done);
    }
  }

  return labels.filter((l, i) => i === 0 || l !== labels[i - 1]);
}

/** True after spreadsheet exploration tools have finished in the current builder turn. */
export function hasCompletedSpreadsheetAnalysisPhase(messages: Message[] | undefined): boolean {
  return getBuilderCompletedStepLabels(messages).includes("Analyzed spreadsheet");
}

const CoeffLoader: React.FC = () => (
  <span className="thinking-loader-coeff-loader" aria-hidden="true">
    <Sparkle2 fill="#3053ff" />
  </span>
);

export const ThinkingLoader: React.FC<{
  messages?: Message[];
  fallbackText?: string;
}> = ({ messages, fallbackText = "Thinking..." }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedTools, setExpandedTools] = useState<boolean[]>([]);
  const [displayedText, setDisplayedText] = useState("");
  const [showPatienceMessage, setShowPatienceMessage] = useState(false);
  const [patienceIndex, setPatienceIndex] = useState(0);
  const timeoutRef = useRef<number | null>(null);
  const patienceTimerRef = useRef<number | null>(null);
  const textStartTimeRef = useRef<number>(Date.now());

  const { deduplicatedCompletedTools, latestToolCall, latestToolText } =
    analyzeInFlightToolChain(messages);

  const hasCompletedTools = deduplicatedCompletedTools.length > 0;
  const displayMessage =
    latestToolCall && getToolLoadingMessage(latestToolCall).trim()
      ? getToolLoadingMessage(latestToolCall)
      : latestToolCall
      ? "Reasoning"
      : fallbackText;
  const displayedToolText = showPatienceMessage ? PATIENCE_MESSAGES[patienceIndex] : latestToolText;

  useEffect(() => {
    setExpandedTools(new Array(deduplicatedCompletedTools.length).fill(false));
  }, [deduplicatedCompletedTools.length]);

  useEffect(() => {
    textStartTimeRef.current = Date.now();
    setShowPatienceMessage(false);
    setPatienceIndex(0);

    if (patienceTimerRef.current) {
      window.clearInterval(patienceTimerRef.current);
      patienceTimerRef.current = null;
    }

    if (!latestToolText) {
      return;
    }

    patienceTimerRef.current = window.setInterval(() => {
      const elapsed = Date.now() - textStartTimeRef.current;
      const cyclePosition = elapsed % (PATIENCE_INTERVAL_MS + PATIENCE_DISPLAY_MS);

      if (elapsed >= PATIENCE_INTERVAL_MS) {
        if (cyclePosition < PATIENCE_DISPLAY_MS) {
          setShowPatienceMessage(true);
          const cycleNumber = Math.floor(
            (elapsed - PATIENCE_INTERVAL_MS) / (PATIENCE_INTERVAL_MS + PATIENCE_DISPLAY_MS)
          );
          setPatienceIndex(cycleNumber % PATIENCE_MESSAGES.length);
        } else {
          setShowPatienceMessage(false);
        }
      }
    }, 500);

    return () => {
      if (patienceTimerRef.current) {
        window.clearInterval(patienceTimerRef.current);
        patienceTimerRef.current = null;
      }
    };
  }, [latestToolText]);

  useEffect(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    setDisplayedText("");

    if (!displayMessage) {
      return;
    }

    let currentIndex = 0;
    const typeNextChar = () => {
      if (currentIndex < displayMessage.length) {
        setDisplayedText(displayMessage.slice(0, currentIndex + 1));
        currentIndex++;
        timeoutRef.current = window.setTimeout(typeNextChar, 30);
      }
    };

    timeoutRef.current = window.setTimeout(typeNextChar, 30);

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [displayMessage]);

  const toggleTool = (index: number) => {
    setExpandedTools(prev => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  return (
    <div className="flex max-w-full flex-col bg-transparent">
      <div
        className={`max-w-full rounded-lg bg-transparent ${
          hasCompletedTools && isExpanded ? "border border-gray-200" : "border border-transparent"
        }`}
      >
        <div
          className={`flex items-center justify-start gap-2 ${
            isExpanded ? "px-3 py-2" : "p-2"
          } select-none`}
          role={hasCompletedTools ? "button" : undefined}
          onClick={() => hasCompletedTools && setIsExpanded(!isExpanded)}
          style={{ cursor: hasCompletedTools ? "pointer" : "default" }}
        >
          <div className="flex h-6 w-6 shrink-0 items-center justify-center">
            <CoeffLoader />
          </div>
          <div className="flex-1 min-w-0 break-words wrap-anywhere text-[13px] font-semibold text-black">
            {displayedText}
          </div>
          {hasCompletedTools && <Chevron isExpanded={isExpanded} />}
        </div>

        {displayedToolText && (
          <div className="pb-3 pl-10 pr-3 pt-1">
            <span className="thinking-loader-pulse block whitespace-pre-wrap break-words wrap-anywhere text-xs leading-5 text-[#727272]">
              {displayedToolText}
            </span>
          </div>
        )}

        {hasCompletedTools && (
          <div
            className={`overflow-hidden transition-[max-height] duration-300 ${
              isExpanded ? "max-h-[2000px]" : "max-h-0"
            }`}
          >
            <div className="flex flex-col gap-0 px-3 pb-3 pl-4">
              {deduplicatedCompletedTools.map((item, index) => {
                const toolMessage = getToolDoneMessage(item.toolCall);
                const hasContent = Boolean(item.reasoning || item.text);

                return (
                  <div key={`${item.messageId}-tool-${index}`} className="flex flex-col gap-0 py-2">
                    <div
                      className="flex items-center gap-2"
                      role={hasContent ? "button" : undefined}
                      onClick={() => hasContent && toggleTool(index)}
                      style={{ cursor: hasContent ? "pointer" : "default", userSelect: "none" }}
                    >
                      <div className="flex-1 min-w-0 break-words wrap-anywhere text-[13px] font-medium text-gray-900">
                        {toolMessage}
                      </div>
                      {hasContent && <Chevron isExpanded={expandedTools[index]} />}
                    </div>
                    {hasContent && (
                      <div
                        className={`overflow-hidden transition-[max-height] duration-300 ${
                          expandedTools[index] ? "max-h-[2000px]" : "max-h-0"
                        }`}
                      >
                        <div className="whitespace-pre-wrap break-words wrap-anywhere pt-2 text-xs leading-5 text-gray-500">
                          {item.reasoning}
                          {item.reasoning && item.text && (
                            <>
                              <br />
                              <br />
                            </>
                          )}
                          {item.text}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes thinkingLoaderCoeffPulse {
          0%, 100% {
            opacity: 0.55;
            transform: scale(0.9);
          }
          50% {
            opacity: 1;
            transform: scale(1.08);
          }
        }
        @keyframes thinkingLoaderGentlePulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        .thinking-loader-coeff-loader {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #3053ff;
          animation: thinkingLoaderCoeffPulse 1.4s ease-in-out infinite;
          transform-origin: center;
        }
        .thinking-loader-coeff-loader svg {
          width: 18px;
          height: 18px;
        }
        .thinking-loader-pulse {
          animation: thinkingLoaderGentlePulse 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
